import React, { useState, useEffect } from 'react';
import { fetchUserConnections, saveDatabaseConfiguration, testDatabaseCredentials, DatabaseConfiguration, updateConnectionStatus, setConnectionActive, deleteConnection, DbType, updateConnection, UpdateConnectionPayload } from '../services/databaseService';
import { getConnectionId, saveConnectionId } from '../utils/storage';

interface DatabaseConnectionDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onConnectionChange?: () => void;
}

const DatabaseConnectionDetails: React.FC<DatabaseConnectionDetailsProps> = ({ isOpen, onClose, onBack, onConnectionChange }) => {
  const [connections, setConnections] = useState<any[]>([]);
  const [expandedConnectionId, setExpandedConnectionId] = useState<string | null>(null);
  const [activeConnectionId, setActiveConnectionId] = useState<string | null>(getConnectionId());
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeletingConnection, setIsDeletingConnection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingConnection, setEditingConnection] = useState<any | null>(null);
  const [editConfig, setEditConfig] = useState<UpdateConnectionPayload>({});

  const [newConfig, setNewConfig] = useState<DatabaseConfiguration>({
    connection_name: '',
    db_type: 'mysql',
    db_host: '',
    db_port: 3306,
    db_name: '',
    db_user: '',
    db_password: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      loadConnections();
    }
  }, [isOpen]);

  const loadConnections = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchUserConnections();
      if (Array.isArray(response)) {
        setConnections(response);

        // If no active connection is set, or the set one isn't in the list,
        // and we have connections, set the first one as active
        const currentId = getConnectionId();
        if (response.length > 0 && (!currentId || !response.find((c: any) => c.id.toString() === currentId))) {
          const firstId = response[0].id.toString();
          saveConnectionId(firstId);
          setActiveConnectionId(firstId);
          // Also update status on backend
          updateConnectionStatus(firstId, true).catch((err: any) => console.error('Failed to update connection status on backend:', err));
        } else {
          setActiveConnectionId(currentId);
        }
        // Always show list view — do NOT auto-select a connection
      } else if (response) {
        setConnections([response]);
        // Do NOT auto-select; always show list first
        const id = response.id.toString();
        const currentId = getConnectionId();
        if (!currentId) {
          saveConnectionId(id);
          setActiveConnectionId(id);
          updateConnectionStatus(id, true).catch((err: any) => console.error('Failed to update connection status on backend:', err));
        } else {
          setActiveConnectionId(currentId);
        }
      } else {
        setError('No database connections found. Please configure your database connection.');
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      setError('Failed to load connections. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log(`${label} copied to clipboard`);
    });
  };

  const handleBackToList = () => {
    setIsCreating(false);
    setError(null);
    setSuccessMessage(null);
    setExpandedConnectionId(null);
    // Refresh active connection ID from storage
    setActiveConnectionId(getConnectionId());
  };

  const handleToggleConnection = (conn: any) => {
    const id = conn.id.toString();
    // Toggle expansion - if already expanded, collapse it
    if (expandedConnectionId === id) {
      setExpandedConnectionId(null);
    } else {
      setExpandedConnectionId(id);
    }
  };

  const handleActivateConnection = async (conn: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card toggle
    const id = conn.id.toString();

    try {
      // Update status on backend first
      await updateConnectionStatus(id, true);

      // Record last_active_at so login restores the correct connection
      await setConnectionActive(id);

      saveConnectionId(id);
      setActiveConnectionId(id);
      setSuccessMessage(`Activated connection: ${conn.connection_name || id}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Notify parent so the dashboard can refresh widget grid immediately
      onConnectionChange?.();
    } catch (err: any) {
      setError(`Failed to activate connection: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteConnection = async (connectionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card toggle

    if (!window.confirm('Are you sure you want to delete this connection? This action cannot be undone.')) {
      return;
    }

    setIsDeletingConnection(true);
    setError(null);

    try {
      await deleteConnection(connectionId);
      setSuccessMessage('Connection deleted successfully');

      // If the deleted connection was active, clear it
      if (activeConnectionId === connectionId) {
        saveConnectionId('');
        setActiveConnectionId(null);
      }

      // Refresh the connections list
      await loadConnections();
      setExpandedConnectionId(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(`Failed to delete connection: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsDeletingConnection(false);
    }
  };

  const handleStartEditing = (conn: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConnection(conn);
    setEditConfig({
      connection_name: conn.connection_name || '',
      db_type: conn.db_type || 'mysql',
      db_host: conn.db_host || '',
      db_port: conn.db_port || 3306,
      db_name: conn.db_name || '',
      db_user: conn.db_user || '',
      db_password: '',
    });
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingConnection(null);
    setEditConfig({});
    setError(null);
    setSuccessMessage(null);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditConfig(prev => ({
      ...prev,
      [name]: name === 'db_port' ? parseInt(value) || 0 : value
    }));
  };

  const getDefaultPort = (value: DbType) => {
    if (value === 'mysql') return 3306;
    if (value === 'postgresql') return 5432;
    return 1433;
  };

  const getDatabaseTypeLabel = (value?: string) => {
    if (value === 'mssql') return 'Microsoft SQL Server';
    if (value === 'postgresql') return 'PostgreSQL';
    return 'MySQL';
  };

  const getDatabaseTypeBadge = (value?: string) => {
    if (value === 'mssql') return 'MSSQL';
    if (value === 'postgresql') return 'PostgreSQL';
    return 'MySQL';
  };

  const handleEditDbTypeChange = (value: DbType) => {
    const defaultPort = getDefaultPort(value);
    setEditConfig(prev => ({
      ...prev,
      db_type: value,
      db_port: defaultPort
    }));
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingConnection) return;
    setIsUpdating(true);
    setError(null);
    try {
      // Only send fields that have values (don't send empty password)
      const payload: UpdateConnectionPayload = { ...editConfig };
      if (!payload.db_password) delete payload.db_password;
      await updateConnection(editingConnection.id.toString(), payload);
      setSuccessMessage('Connection updated successfully!');
      await loadConnections();
      setTimeout(() => {
        setEditingConnection(null);
        setEditConfig({});
        setSuccessMessage(null);
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update connection');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTestEditConnection = async () => {
    setIsTesting(true);
    setError(null);
    try {
      const testCreds = {
        db_host: editConfig.db_host || '',
        db_port: editConfig.db_port || 3306,
        db_name: editConfig.db_name || '',
        db_user: editConfig.db_user || '',
        db_password: editConfig.db_password || '',
        db_type: editConfig.db_type,
      };
      const result = await testDatabaseCredentials(testCreds);
      if (result.success) {
        setSuccessMessage('Connection test successful!');
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewConfig(prev => ({
      ...prev,
      [name]: name === 'db_port' ? parseInt(value) || 0 : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleDbTypeChange = (value: DbType) => {
    const defaultPort = getDefaultPort(value);
    setNewConfig(prev => ({
      ...prev,
      db_type: value,
      db_port: defaultPort
    }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!newConfig.connection_name) errors.connection_name = 'Connection name is required';
    if (!newConfig.db_host) errors.db_host = 'Host is required';
    if (!newConfig.db_port) errors.db_port = 'Port is required';
    if (!newConfig.db_name) errors.db_name = 'Database name is required';
    if (!newConfig.db_user) errors.db_user = 'Username is required';
    if (!newConfig.db_password) errors.db_password = 'Password is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;
    setIsTesting(true);
    setError(null);
    try {
      const result = await testDatabaseCredentials(newConfig);
      if (result.success) {
        setSuccessMessage('Connection test successful!');
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const handleCreateConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await saveDatabaseConfiguration(newConfig);
      setSuccessMessage('Connection created successfully!');
      setTimeout(() => {
        setIsCreating(false);
        loadConnections();
        setSuccessMessage(null);
        setIsSubmitting(false);
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create connection');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-2xl max-h-[80vh] shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col relative">
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm z-50 rounded-lg flex items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
              <div className="text-center">
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">Creating Connection...</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Please wait while we save your configuration</p>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <button
              onClick={isCreating ? handleBackToList : editingConnection ? handleCancelEdit : onBack}
              disabled={isSubmitting || isUpdating}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
                arrow_back
              </span>
            </button>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              {isCreating ? 'New Connection' : editingConnection ? 'Edit Connection' : 'Connections'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
              close
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading connections...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64">
              <span className="material-symbols-outlined text-red-500 mb-2" style={{ fontSize: '48px' }}>
                error
              </span>
              <p className="text-red-600 dark:text-red-400 text-center mb-4">{error}</p>
              <button
                onClick={loadConnections}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : editingConnection ? (
            /* ── EDIT FORM ── */
            <form onSubmit={handleSaveEdit} className="flex flex-col flex-1 overflow-hidden">
              <div className="px-6 pt-4 pb-2 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {successMessage && (
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm flex items-center">
                      <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>check_circle</span>
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm flex items-center">
                      <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>error</span>
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    {/* ── Editable: Connection Name ── */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Connection Name</label>
                      <input
                        type="text"
                        name="connection_name"
                        value={editConfig.connection_name || ''}
                        onChange={handleEditInputChange}
                        disabled={isUpdating}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="e.g. Production Database"
                      />
                    </div>

                    {/* ── Read-only fields ── */}
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
                      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                        <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '14px' }}>lock</span>
                        <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Read-only — cannot be changed after creation</span>
                      </div>
                      <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        <div className="flex justify-between items-center px-3 py-2.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Database Type</span>
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">
                            {getDatabaseTypeLabel(editConfig.db_type)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center px-3 py-2.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Host</span>
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">{editConfig.db_host}</span>
                        </div>
                        <div className="flex justify-between items-center px-3 py-2.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Port</span>
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">{editConfig.db_port}</span>
                        </div>
                        <div className="flex justify-between items-center px-3 py-2.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Database Name</span>
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">{editConfig.db_name}</span>
                        </div>
                        <div className="flex justify-between items-center px-3 py-2.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">Username</span>
                          <span className="text-xs font-mono font-medium text-slate-700 dark:text-slate-300">{editConfig.db_user}</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Editable: Password ── */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Password</label>
                      <input
                        type="password"
                        name="db_password"
                        value={editConfig.db_password || ''}
                        onChange={handleEditInputChange}
                        disabled={isUpdating}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Leave blank to keep existing"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Sticky action footer */}
              <div className="px-6 pb-5 pt-3 border-t border-slate-200 dark:border-slate-700 flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isUpdating}
                  className="py-2.5 px-4 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleTestEditConnection}
                  disabled={isTesting || isUpdating}
                  className="flex-1 py-2.5 px-4 border border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isTesting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div> : null}
                  Test Connection
                </button>
                <button
                  type="submit"
                  disabled={isUpdating || isTesting}
                  className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isUpdating ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : null}
                  Save Changes
                </button>
              </div>
            </form>
          ) : isCreating ? (
            <form onSubmit={handleCreateConnection} className="flex flex-col flex-1 overflow-hidden">
              <div className="px-6 pt-4 pb-2 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {successMessage && (
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm flex items-center">
                      <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>check_circle</span>
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm flex items-center">
                      <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>error</span>
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Connection Name</label>
                      <input
                        type="text"
                        name="connection_name"
                        value={newConfig.connection_name}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.connection_name ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                        placeholder="e.g. Production Database"
                      />
                      {formErrors.connection_name && <p className="text-red-500 text-[10px] mt-1">{formErrors.connection_name}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Database Type</label>
                      <select
                        name="db_type"
                        value={newConfig.db_type}
                        onChange={(e) => handleDbTypeChange(e.target.value as DbType)}
                        disabled={isSubmitting}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="mysql">MySQL</option>
                        <option value="mssql">Microsoft SQL Server</option>
                        <option value="postgresql">PostgreSQL</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Host</label>
                        <input
                          type="text"
                          name="db_host"
                          value={newConfig.db_host}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.db_host ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                          placeholder="localhost"
                        />
                        {formErrors.db_host && <p className="text-red-500 text-[10px] mt-1">{formErrors.db_host}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Port</label>
                        <input
                          type="number"
                          name="db_port"
                          value={newConfig.db_port}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.db_port ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                          placeholder="3306"
                        />
                        {formErrors.db_port && <p className="text-red-500 text-[10px] mt-1">{formErrors.db_port}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Database Name</label>
                      <input
                        type="text"
                        name="db_name"
                        value={newConfig.db_name}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.db_name ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                        placeholder="my_database"
                      />
                      {formErrors.db_name && <p className="text-red-500 text-[10px] mt-1">{formErrors.db_name}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Username</label>
                        <input
                          type="text"
                          name="db_user"
                          value={newConfig.db_user}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.db_user ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                          placeholder="root"
                        />
                        {formErrors.db_user && <p className="text-red-500 text-[10px] mt-1">{formErrors.db_user}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Password</label>
                        <input
                          type="password"
                          name="db_password"
                          value={newConfig.db_password}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className={`w-full p-2.5 bg-slate-50 dark:bg-slate-900 border ${formErrors.db_password ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                          placeholder="••••••••"
                        />
                        {formErrors.db_password && <p className="text-red-500 text-[10px] mt-1">{formErrors.db_password}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Sticky action footer — always visible, outside the scrollable area */}
              <div className="px-6 pb-5 pt-3 border-t border-slate-200 dark:border-slate-700 flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={isTesting || isSubmitting}
                  className="flex-1 py-2.5 px-4 border border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isTesting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div> : null}
                  Test Connection
                </button>
                <button
                  type="button"
                  onClick={(e) => handleCreateConnection(e as any)}
                  disabled={isSubmitting || isTesting}
                  className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : null}
                  Save Connection
                </button>
              </div>
            </form>
          ) : (
            <div className="p-6 overflow-y-auto h-full">
              {successMessage && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm flex items-center">
                  <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>check_circle</span>
                  {successMessage}
                </div>
              )}
              <div className="space-y-4">
                {connections.map((conn, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${activeConnectionId === conn.id.toString()
                      ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    onClick={() => handleToggleConnection(conn)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {conn.connection_name || `Connection ${index + 1}`}
                          </span>
                          {activeConnectionId === conn.id.toString() && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 mr-1.5">
                            {getDatabaseTypeBadge(conn.db_type)}
                          </span>
                          {conn.db_host}:{conn.db_port} • {conn.db_name}
                        </p>
                      </div>
                      <div className="ml-4">
                        <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '16px' }}>
                          {expandedConnectionId === conn.id.toString() ? 'expand_less' : 'expand_more'}
                        </span>
                      </div>
                    </div>

                    {/* Expanded content */}
                    {expandedConnectionId === conn.id.toString() && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                          Connection Details:
                        </h4>
                        <div className="space-y-2 mb-3">
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">Host:</span>
                            <span className="text-slate-700 dark:text-slate-300 font-mono">{conn.db_host}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">Port:</span>
                            <span className="text-slate-700 dark:text-slate-300 font-mono">{conn.db_port}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">Database:</span>
                            <span className="text-slate-700 dark:text-slate-300 font-mono">{conn.db_name}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500 dark:text-slate-400">Username:</span>
                            <span className="text-slate-700 dark:text-slate-300 font-mono">{conn.db_user}</span>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2">
                          {/* Edit button — always visible */}
                          <button
                            onClick={(e) => handleStartEditing(conn, e)}
                            className="px-3 py-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors flex items-center space-x-1"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>edit</span>
                            <span>Edit</span>
                          </button>
                          {activeConnectionId !== conn.id.toString() && (
                            <>
                              <button
                                onClick={(e) => handleActivateConnection(conn, e)}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                              >
                                <span>Switch</span>
                              </button>
                              <button
                                onClick={(e) => handleDeleteConnection(conn.id.toString(), e)}
                                disabled={isDeletingConnection}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                              >
                                {isDeletingConnection ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                    <span>Deleting...</span>
                                  </>
                                ) : (
                                  <span>Delete</span>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseConnectionDetails;
